<script>
import { getPaymentStatus, getWalletBalance } from '@/api/ads'
import { formatNumber } from '@/views/ads/utils/index'

const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const SUCCESS_POLLING_TIME = 30000 // ms

export default {
  name: 'TopUpSuccess',
  data() {
    return {
      isLoading: true,
      topUpAmount: 0,
      currentBalance: {
        amount: 0,
      },
    }
  },
  computed: {
    successDesc() {
      return this.$t('ads_top_up_success_desc', {
        value: formatNumber(this.topUpAmount),
      })
    },
    topUpAsNeededDesc() {
      return this.$t('ads_top_up_as_needed', {
        value: formatNumber(Number(this.currentBalance?.amount || 0)),
      })
    },
  },
  mounted() {
    this.getStatus()
  },
  methods: {
    async getStatus() {
      try {
        this.isLoading = true
        const startTime = Date.now()

        while (true) {
          const { data } = await getPaymentStatus(this.$route.query.id)
          const { data: topUpStatus } = data

          // If Pending => wait for 1000 ms then process to get status again
          // unless it pass the SUCCESS_POLLING_TIME
          if (topUpStatus.status === 'Pending') {
            await waitFor(1000)
            continue
          }

          // If Success => show success popup
          if (topUpStatus.status === 'Success') {
            const { data: walletBalanceData } = await getWalletBalance()

            this.currentBalance = walletBalanceData.data.balance
            this.topUpAmount = Number(topUpStatus.netAmount)
            this.isPending = false
            break
          }

          // In case it's Declined, this case should not the exist
          // but still handle just in case
          if (topUpStatus.status === 'Declined') {
            this.$router.replace('/ads/top-up-failed')
            break
          }

          // Check if the process has been checking for more than SUCCESS_POLLING_TIME
          // if so => stop and show message that payment still processing
          const timePassedSinceStart = Date.now() - startTime
          if (timePassedSinceStart > SUCCESS_POLLING_TIME) {
            this.$router.replace('/ads/top-up-pending')
            break
          }
        }
      } catch {
        this.$router.replace('/ads/top-up-failed')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<template>
  <div class="top-up-success">
    <template v-if="isLoading">
      <div class="payment-processing">
        <i class="el-icon-loading" />
        <h1>{{ $t('ads_top_up_processing_message') }}</h1>
      </div>
    </template>

    <template v-else>
      <div class="icon-container success-icon">
        <svg-icon icon-class="circled-check" />
      </div>
      <h1>{{ $t('ads_top_up_success_title') }}</h1>
      <p>
        {{ successDesc }}
      </p>
      <div class="current-balance">
        <h3>{{ $t('ads_top_up_success_curr_balance') }}</h3>
        <div class="balance-value">
          <template v-if="isLoading">
            {{ $t('ads_balance_loading') }}
          </template>
          <template v-else>
            {{ currentBalance | amountFormatter }}
          </template>
        </div>
      </div>
      <p>
        {{ topUpAsNeededDesc }}
      </p>
      <router-link to="/ads/create" class="primary-cta-btn mb-3" replace>
        {{ $t('ads_top_up_success_create_campaign') }}
      </router-link>
      <router-link to="/ads/dashboard" class="secondary-cta-btn" replace>
        {{ $t('ads_top_up_success_to_dashboard') }}
      </router-link>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@mixin direction-aware-font {
  [dir='ltr'] & {
    font-family: 'Space Grotesk', sans-serif;
  }
  [dir='rtl'] & {
    font-family: 'IBM Plex Sans Arabic', sans-serif;
  }
}

.success-icon {
  background: #93ba301a;

  svg {
    color: #93ba30;
  }
}

.current-balance {
  width: 100%;
  padding: 14px;
  margin-block-end: 28px;
  background: #f2f2f2;
  border-radius: 12px;
}

.balance-value {
  @include direction-aware-font;

  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: #000;
}

h3 {
  margin-block-end: 6px;
  font-size: 14px;
  line-height: 22px;
  color: #666;
}

.payment-processing {
  i {
    margin-block-end: 20px;
    font-size: 64px;
    color: #ab8dff;
  }
}

.top-up-success {
  display: contents;
}
</style>
